import { Link } from "react-router-dom";
import { Phone, Mail, Clock, ArrowRight, MapPin, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-[#0b1a1e] text-white">
      {/* CTA Banner — solid dark, no gradient */}
      <div className="bg-primary">
        <div className="container-max px-4 py-12 md:py-14">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready for a Spotless Space?
              </h2>
              <p className="text-primary-foreground/85 text-sm md:text-base mt-1">
                Free estimates across the Memphis area · {siteConfig.hours.summary}.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold"
              >
                Get Free Estimate <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                asChild
                size="lg"
                className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
              >
                <a href={siteConfig.phoneHref}>
                  <Phone className="h-4 w-4" /> {siteConfig.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer content — solid dark BG, no gradient */}
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            {/* White card around logo per brief */}
            <div className="inline-flex items-center justify-center bg-white rounded-2xl p-3 shadow-md mb-5">
              <img
                src={siteConfig.logo}
                alt={siteConfig.name}
                className="h-16 w-auto"
                width={360}
                height={186}
                loading="lazy"
                decoding="async"
              />
            </div>
            <h3 className="font-heading text-xl text-white font-bold mb-2 tracking-tight">
              {siteConfig.name}
            </h3>
            <p className="text-white/70 text-sm leading-relaxed">
              Locally owned house and office cleaning based in Germantown, TN — serving Collierville,
              Cordova, Bartlett, Eads, and Memphis, Monday through Friday.
            </p>
            <div className="mt-4 bg-secondary/15 border border-secondary/30 rounded-xl px-4 py-3">
              <p className="text-white font-semibold text-sm">
                ✨ Free, no-pressure estimates
              </p>
            </div>
            {siteConfig.social.instagram && (
              <a
                href={siteConfig.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
              >
                <Instagram className="h-4 w-4 text-secondary" /> @mglcleaningsolutions
              </a>
            )}
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Services
            </h3>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="footer-link-underline text-white/70 hover:text-white text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <Link
                  to="/services"
                  className="footer-link-underline text-secondary hover:text-secondary/80 text-sm font-semibold transition-colors"
                >
                  View All Services →
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Service Areas
            </h3>
            <ul className="grid grid-cols-2 gap-x-2 gap-y-2 text-sm">
              {locations.slice(0, 8).map((loc) => (
                <li key={loc.slug}>
                  <Link
                    to={`/locations/${loc.slug}`}
                    className="footer-link-underline text-white/70 hover:text-white transition-colors"
                  >
                    {loc.name}, {loc.state}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/locations"
              className="mt-3 inline-block footer-link-underline text-secondary hover:text-secondary/80 text-sm font-semibold transition-colors"
            >
              All Locations →
            </Link>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Contact
            </h3>
            <ul className="space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <a href={siteConfig.phoneHref} className="inline-flex items-center min-h-[24px] hover:text-white transition-colors">
                  {siteConfig.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <a href={siteConfig.emailHref} className="inline-flex items-center min-h-[24px] hover:text-white transition-colors break-all">
                  {siteConfig.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <span>
                  {siteConfig.address.street}<br />
                  {siteConfig.address.city}, {siteConfig.address.state} {siteConfig.address.postalCode}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <div>
                  <div>{siteConfig.hours.days}</div>
                  <div className="text-white/80 text-xs">{siteConfig.hours.time}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/75 text-xs">
            © {new Date().getFullYear()} {siteConfig.legalName}. All rights reserved.
          </p>
          <p className="text-white/75 text-xs">
            House & Office Cleaning · Germantown & the Memphis Area, TN
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
